import Button from 'shared/Buttons/Button/Button'
import { useEffect, useState } from 'react'
import { IProjectCheckpointArtifact } from 'screens/Admin/ProjectCheckpoints/projectCheckpoints.types'
import { tinyUuid } from 'utils/tiny-uuid'
import projectCheckpointsApi from 'screens/Admin/ProjectCheckpoints/projectCheckpoints.api'
import useHttpLoader from 'shared/hooks/useHttpLoader'
import { useSetRecoilState } from 'recoil'
import eventBus from 'services/eventBus'
import ConfirmDeleteModal from 'shared/Modal/ConfirmDeleteModal'
import { useCheckpointArtifactsState } from 'screens/Admin/ProjectCheckpoints/hooks/useCheckpointArtifactsState'
import checkpointsArtifactsAtom from 'screens/Admin/ProjectCheckpoints/checkpointArtifacts.atom'
import ArtifactSheet from 'screens/Admin/ProjectCheckpoints/components/ArtifactSheet'

interface CheckpointArtifactsProps {
  checkpointId: string
}

const CheckpointArtifacts = (props: CheckpointArtifactsProps) => {
  const { artifactsState, artifactsMap } = useCheckpointArtifactsState(props.checkpointId)
  const [artifactsDraft, setArtifactsDraft] = useState(artifactsState)
  const [artifactIdToDelete, setArtifactIdToDelete] = useState<string | null>()

  const { wait } = useHttpLoader()

  const setartifactsState = useSetRecoilState(checkpointsArtifactsAtom)

  useEffect(() => {
    setArtifactsDraft(artifactsState)
  }, [artifactsState])

  const isValid = (artifact: IProjectCheckpointArtifact) =>
    artifact && !!artifact.title // TODO добавить валидацию

  const handleSave = (id: string) => () => {
    const artifactDraft = artifactsDraft.find((c) => c.id === id)
    if(!isValid(artifactDraft)) {
      eventBus.pub('ntf.message', { message: 'Заполните обязательные поля', type: 'error' })
      return
    }
    const artifact = artifactsMap[id]
    const promise = artifact
      ? projectCheckpointsApi.updateArtifact(props.checkpointId, id, artifactDraft)
      : projectCheckpointsApi.createArtifact(props.checkpointId, artifactDraft)

    wait(promise, (resp) => {
      if (resp.status === 'success') {
        setartifactsState((prev) => ({
          ...prev,
          items: {
            ...prev.items,
            [props.checkpointId]: [resp.body, ...prev.items[props.checkpointId].filter((c) => c.id !== id)],
          },
        }))
        eventBus.pub('ntf.message', { message: 'Артефакт сохранен', type: 'info' })
      }
    })
  }

  const handleAdd = () => {
    const defaultArtifact: IProjectCheckpointArtifact = {
      id: tinyUuid(),
      type: "TEXT",
      title: '',
      description: '',
      maxSymbols: 10000,
      minSymbols: 0,
    }
    setArtifactsDraft((prev) => [defaultArtifact, ...prev])
  }

  const hasUnsavedChanges = (artifactDtraft: IProjectCheckpointArtifact) => {
    const artifact = artifactsMap[artifactDtraft.id]
    if (!artifact) return true
    let hasUnsavedChanges = false
    for (const key in artifactDtraft) {
      if (artifactDtraft[key] !== artifact[key]) hasUnsavedChanges = true
    }
    return hasUnsavedChanges
  }

  const handleChange = (id: string) => (val: any, name: string) => {
    setArtifactsDraft((prev) => {
      const newCriteria = prev.map((c) => (c.id === id ? { ...c, [name]: val } : c))
      return newCriteria
    })
  }
  const handleDelete = () => {
    const artifact = artifactsMap[artifactIdToDelete!]
    if (artifact) {
      wait(projectCheckpointsApi.deleteArtifact(props.checkpointId, artifactIdToDelete), (resp) => {
        if (resp.status === 'success') {
          setartifactsState((prev) => ({
            ...prev,
            items: {
              ...prev.items,
              [props.checkpointId]: prev.items[props.checkpointId].filter((c) => c.id !== artifactIdToDelete),
            },
          }))
          eventBus.pub('ntf.message', { message: 'Критерий удален', type: 'info' })
        }
      })
    }
    setArtifactsDraft((prev) => prev.filter((c) => c.id !== artifactIdToDelete))
    setArtifactIdToDelete(null)
  }

  const isDisabledAddNew = () => artifactsDraft.some((c) => hasUnsavedChanges(c))

  return (
    <>
      {artifactIdToDelete && (
        <ConfirmDeleteModal onDelete={handleDelete} onClose={() => setArtifactIdToDelete(null)} entityName='Артефакт' />
      )}
      <div className='flex-lines'>
        <Button theme='grey-outline' style={{ width: 'fit-content' }} onClick={handleAdd} disabled={isDisabledAddNew()}>
          Добавить артефакт
        </Button>
        <div className='grid-form-2'>
          {artifactsDraft.map((artifact) => (
            <ArtifactSheet artifact={artifact} onChange={handleChange(artifact.id)} key={artifact.id}>
              <div className='form-footer'>
                <div className='inline-flex-gap'>
                  <Button theme='primary' disabled={!hasUnsavedChanges(artifact)} onClick={handleSave(artifact.id)}>
                    Сохранить
                  </Button>
                  <Button theme='danger' onClick={() => setArtifactIdToDelete(artifact.id)}>
                    Удалить
                  </Button>
                </div>
              </div>
            </ArtifactSheet>
          ))}
        </div>
      </div>
    </>
  )
}

export default CheckpointArtifacts
